import { useEffect, useState } from 'react'
import { apiBaseUrl } from '../config/api'
import { resolveBannerEvent, resolvePopupEvents } from './discovery'

let cachedEvents = null
let pending = null

export function resetPublicEventsCache() {
  cachedEvents = null
  pending = null
}

function asList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

export function loadPublicEvents() {
  if (cachedEvents) return Promise.resolve(cachedEvents)
  if (pending) return pending
  if (!apiBaseUrl) return Promise.reject(new Error('missing api'))
  pending = fetch(`${apiBaseUrl}/api/events/`)
    .then((response) => {
      const type = response.headers.get('content-type') || ''
      if (!response.ok || !type.includes('application/json')) throw new Error('events')
      return response.json()
    })
    .then((data) => {
      cachedEvents = asList(data)
      return cachedEvents
    })
    .finally(() => {
      pending = null
    })
  return pending
}

export function usePublicEvents() {
  const [state, setState] = useState(() => (
    cachedEvents ? { status: 'ready', events: cachedEvents } : { status: 'loading', events: [] }
  ))
  useEffect(() => {
    let alive = true
    loadPublicEvents()
      .then((events) => {
        if (alive) setState({ status: 'ready', events })
      })
      .catch(() => {
        if (alive) setState({ status: 'error', events: [] })
      })
    return () => {
      alive = false
    }
  }, [])
  return state
}

let cachedConfig = null
let pendingConfig = null

export function loadPromotionConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig)
  if (pendingConfig) return pendingConfig
  if (!apiBaseUrl) return Promise.reject(new Error('missing api'))
  pendingConfig = fetch(`${apiBaseUrl}/api/site/event-promotion/`, { cache: 'no-store' })
    .then((response) => {
      const type = response.headers.get('content-type') || ''
      if (!response.ok || !type.includes('application/json')) throw new Error('promotion')
      return response.json()
    })
    .then((data) => {
      cachedConfig = data
      return cachedConfig
    })
    .finally(() => {
      pendingConfig = null
    })
  return pendingConfig
}

export function usePromotionConfig() {
  const [state, setState] = useState(() => (
    cachedConfig ? { status: 'ready', config: cachedConfig } : { status: 'loading', config: null }
  ))
  useEffect(() => {
    let alive = true
    loadPromotionConfig()
      .then((config) => {
        if (alive) setState({ status: 'ready', config })
      })
      .catch(() => {
        if (alive) setState({ status: 'error', config: null })
      })
    return () => {
      alive = false
    }
  }, [])
  return state
}

export function useEventDiscovery(intervalMs) {
  const eventsState = usePublicEvents()
  const configState = usePromotionConfig()
  const now = useNow(intervalMs)
  const ready = eventsState.status === 'ready' && configState.status === 'ready'
  return {
    ready,
    now,
    bannerEvent: ready ? resolveBannerEvent(eventsState.events, configState.config, now) : null,
    slides: ready ? resolvePopupEvents(eventsState.events, configState.config, now) : [],
  }
}

export function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = () => setNow(Date.now())
    const id = window.setInterval(tick, intervalMs)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs])
  return now
}
